'use client'

import { createContext, useContext, useMemo, useState } from 'react'

import type { TopNavItem } from './TopNavProps'
import { type TopNavProps } from './TopNavProps'

interface TopNavContextType extends TopNavProps {
  expandedMenu: TopNavItem | null
  setExpandedMenu: (menu: TopNavItem | null) => void

  isExpanded: boolean
  setExpanded: (isExpanded: boolean) => void

  isMobileSubExpanded: boolean
  setMobileSubExpanded: (isExpanded: boolean) => void
}

export const TopNavContext = createContext<TopNavContextType>({
  isMobileSubExpanded: false,
  setMobileSubExpanded: () => {},

  expandedMenu: null,
  setExpandedMenu: () => {},

  isExpanded: false,
  setExpanded: () => {},
})

export const TopNavProvider = ({
  children,
  topNavProps,
}: {
  children: React.ReactNode
  topNavProps: TopNavProps
}) => {
  const [isExpanded, setExpanded] = useState(false)

  const [isMobileSubExpanded, setMobileSubExpanded] = useState(false)

  const [expandedMenu, setExpandedMenu] = useState<TopNavItem | null>(null)

  const value = useMemo(
    () => ({
      ...topNavProps,
      isExpanded,
      setExpanded,

      isMobileSubExpanded,
      setMobileSubExpanded,

      expandedMenu,
      setExpandedMenu,
    }),
    [topNavProps, isExpanded, isMobileSubExpanded, expandedMenu]
  )

  return <TopNavContext.Provider value={value}>{children}</TopNavContext.Provider>
}

export const useTopNav = () => {
  const context = useContext(TopNavContext)
  if (!context) {
    throw new Error('useTopNav must be used within a TopNavProvider')
  }
  return context
}
